import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  BankDetail,
  BankDetailsApi,
  Configuration,
  CreateBankDetailDto,
  ErrorResponse,
  PaginatedBankDetailsDto,
  UpdateBankDetailDto,
} from '@sr-sdks/api-sdk-axios';
import { AxiosError } from 'axios';
import { configuration } from '../../configuration';
import { RootState } from '../../stateStore';
import { ApiLoadingStateEnum } from '../../utils/api/apiLoadingStateEnum';
import axiosInstance, {
  invalidateCacheRequestConfig,
} from '../../utils/axiosInstance';
import { BankDetailsSliceState } from './bankDetailsSliceState';

// Function for creating an instance of the InvitationsApi class
const GetBankDetailsApi = () => {
  return new BankDetailsApi(
    new Configuration(),
    configuration.PAYMENTS_API_BASE,
    axiosInstance,
  );
};

const create = createAsyncThunk(
  'bank-detail/create',
  async (payload: CreateBankDetailDto, thunkApi) => {
    try {
      const response = await GetBankDetailsApi().bankDetailsControllerCreate(
        payload,
        invalidateCacheRequestConfig('bank-details'),
      );

      return response.data;
    } catch (error) {
      return thunkApi.rejectWithValue(
        (error as AxiosError).response?.data as ErrorResponse,
      );
    }
  },
);

const createV2 = createAsyncThunk(
  'bank-detail/createV2',
  async (payload: CreateBankDetailDto, thunkApi) => {
    try {
      const response = await GetBankDetailsApi().bankDetailsControllerCreateV2(
        payload,
        invalidateCacheRequestConfig('bank-details'),
      );

      return response.data;
    } catch (error) {
      return thunkApi.rejectWithValue(
        (error as AxiosError).response?.data as ErrorResponse,
      );
    }
  },
);

const findAll = createAsyncThunk(
  'bank-detail/findAll',
  async (
    {
      page = 1,
      pageSize = 100,
      sortBy = 'isDefault:desc',
      workspaceId,
    }: {
      page?: number;
      pageSize?: number;
      sortBy?: string;
      workspaceId: string;
    },
    thunkApi,
  ) => {
    try {
      const response = await GetBankDetailsApi().bankDetailsControllerFindAll(
        workspaceId,
        page,
        pageSize,
        sortBy,
      );

      return response.data;
    } catch (error) {
      return thunkApi.rejectWithValue(
        (error as AxiosError).response?.data as ErrorResponse,
      );
    }
  },
);

const findOne = createAsyncThunk(
  'bank-detail/findOne',
  async (id: string, thunkApi) => {
    try {
      const response =
        await GetBankDetailsApi().bankDetailsControllerFindOne(id);

      return response.data;
    } catch (error) {
      return thunkApi.rejectWithValue(
        (error as AxiosError).response?.data as ErrorResponse,
      );
    }
  },
);

const update = createAsyncThunk(
  'bank-detail/update',
  async (
    {
      id,
      updateBankDetailDto,
    }: { id: string; updateBankDetailDto: UpdateBankDetailDto },
    thunkApi,
  ) => {
    try {
      const result = await GetBankDetailsApi().bankDetailsControllerUpdate(
        id,
        updateBankDetailDto,
        invalidateCacheRequestConfig('bank-details'),
      );

      return result.data;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

const updateV2 = createAsyncThunk(
  'bank-detail/updateV2',
  async (
    {
      id,
      updateBankDetailDto,
    }: { id: string; updateBankDetailDto: UpdateBankDetailDto },
    thunkApi,
  ) => {
    try {
      const result = await GetBankDetailsApi().bankDetailsControllerUpdateV2(
        id,
        updateBankDetailDto,
        invalidateCacheRequestConfig('bank-details'),
      );

      return result.data;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

const setDefault = createAsyncThunk(
  'bank-detail/setDefault',
  async ({ id }: { id: string }, thunkApi) => {
    try {
      const result = await GetBankDetailsApi().bankDetailsControllerSetDefault(
        id,
        invalidateCacheRequestConfig('bank-details', 'payment-rule'),
      );

      return result.data;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

const remove = createAsyncThunk(
  'bank-detail/remove',
  async (id: string, thunkApi) => {
    try {
      const response = await GetBankDetailsApi().bankDetailsControllerRemove(
        id,
        invalidateCacheRequestConfig('bank-details'),
      );

      return response.data;
    } catch (error) {
      return thunkApi.rejectWithValue(
        (error as AxiosError).response?.data as ErrorResponse,
      );
    }
  },
);

export const initialBankDetailsState: BankDetailsSliceState = {
  bankDetails: [],
  isCreating: ApiLoadingStateEnum.idle,
  isFindOneLoading: ApiLoadingStateEnum.idle,
  isLoading: ApiLoadingStateEnum.idle,
  isRemoving: ApiLoadingStateEnum.idle,
  isSetDefaulting: ApiLoadingStateEnum.idle,
  isUpdating: ApiLoadingStateEnum.idle,
};

function applyCreateFulfilled(
  state: BankDetailsSliceState,
  bankDetail: BankDetail,
) {
  state.isCreating = ApiLoadingStateEnum.succeeded;
  state.bankDetail = bankDetail;

  if (!state.bankDetails.some((item) => item._id === bankDetail._id)) {
    state.bankDetails.push(bankDetail);
  }

  // The list is tagged with the workspace it describes, and `findAll.pending`
  // empties it when that tag names a different workspace. A created account
  // goes straight into the list without a fetch, so the tag has to move with
  // it — otherwise the first account created for a workspace is wiped by the
  // next fetch and only reappears when that response lands.
  state.bankDetailsWorkspaceId = bankDetail.workspaceId;
}

function applyUpdateFulfilled(
  state: BankDetailsSliceState,
  bankDetail: BankDetail,
) {
  state.isUpdating = ApiLoadingStateEnum.succeeded;
  state.bankDetail = bankDetail;

  const index = state.bankDetails.findIndex(
    (item) => item._id === bankDetail._id,
  );

  if (index !== -1) {
    state.bankDetails[index] = bankDetail;
  }
}

function applySetDefaultFulfilled(
  state: BankDetailsSliceState,
  bankDetail: BankDetail,
) {
  state.isSetDefaulting = ApiLoadingStateEnum.succeeded;
  state.bankDetail = bankDetail;

  state.bankDetails = state.bankDetails.map((item) =>
    item._id === bankDetail._id
      ? bankDetail
      : { ...item, isDefault: false },
  );
}

function applyRemoveFulfilled(
  state: BankDetailsSliceState,
  bankDetail: BankDetail,
) {
  state.isRemoving = ApiLoadingStateEnum.succeeded;
  state.bankDetail = bankDetail;

  state.bankDetails = state.bankDetails.filter(
    (item) => item._id !== bankDetail._id,
  );
}

const bankDetailSlice = createSlice({
  extraReducers: (builder) => {
    builder
      // Create
      .addCase(create.fulfilled, (state, action) => {
        applyCreateFulfilled(state, action.payload);
      })
      .addCase(create.pending, (state) => {
        state.isCreating = ApiLoadingStateEnum.loading;
      })
      .addCase(create.rejected, (state, action) => {
        state.isCreating = ApiLoadingStateEnum.failed;
        state.errorResponse = action.payload as ErrorResponse;
      })

      .addCase(createV2.fulfilled, (state, action) => {
        applyCreateFulfilled(state, action.payload);
      })
      .addCase(createV2.pending, (state) => {
        state.isCreating = ApiLoadingStateEnum.loading;
      })
      .addCase(createV2.rejected, (state, action) => {
        state.isCreating = ApiLoadingStateEnum.failed;
        state.errorResponse = action.payload as ErrorResponse;
      })

      // FindAll
      .addCase(findAll.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingStateEnum.succeeded;
        const bankDetails = action.payload as PaginatedBankDetailsDto;

        state.bankDetails = bankDetails as unknown as BankDetail[];
        state.bankDetailsWorkspaceId = action.meta.arg.workspaceId;
      })
      .addCase(findAll.pending, (state, action) => {
        state.isLoading = ApiLoadingStateEnum.loading;

        // Only on a workspace change: a rejected findAll leaves `bankDetails`
        // untouched, so the previous workspace's accounts would otherwise be
        // read as this one's. Refetching the same workspace keeps the list so
        // the empty state can't flash, and the length check avoids a new array
        // identity re-triggering the effects that dispatch findAll.
        const holdsAnotherWorkspace =
          state.bankDetailsWorkspaceId !== undefined &&
          state.bankDetailsWorkspaceId !== action.meta.arg.workspaceId;

        if (holdsAnotherWorkspace && state.bankDetails.length > 0) {
          state.bankDetails = [];
        }
      })
      .addCase(findAll.rejected, (state, action) => {
        state.isLoading = ApiLoadingStateEnum.failed;
        state.errorResponse = action.payload as ErrorResponse;
      })

      // FindOne
      .addCase(findOne.fulfilled, (state, action) => {
        state.isFindOneLoading = ApiLoadingStateEnum.succeeded;
        state.bankDetail = action.payload;
      })
      .addCase(findOne.pending, (state) => {
        state.isFindOneLoading = ApiLoadingStateEnum.loading;
      })
      .addCase(findOne.rejected, (state, action) => {
        state.isFindOneLoading = ApiLoadingStateEnum.failed;
        state.errorResponse = action.payload as ErrorResponse;
      })

      // Update
      .addCase(update.fulfilled, (state, action) => {
        applyUpdateFulfilled(state, action.payload);
      })
      .addCase(update.pending, (state) => {
        state.isUpdating = ApiLoadingStateEnum.loading;
      })
      .addCase(update.rejected, (state, action) => {
        state.isUpdating = ApiLoadingStateEnum.failed;
        state.errorResponse = action.payload as ErrorResponse;
      })

      .addCase(updateV2.fulfilled, (state, action) => {
        applyUpdateFulfilled(state, action.payload);
      })
      .addCase(updateV2.pending, (state) => {
        state.isUpdating = ApiLoadingStateEnum.loading;
      })
      .addCase(updateV2.rejected, (state, action) => {
        state.isUpdating = ApiLoadingStateEnum.failed;
        state.errorResponse = action.payload as ErrorResponse;
      })

      // SetDefault
      .addCase(setDefault.fulfilled, (state, action) => {
        applySetDefaultFulfilled(state, action.payload);
      })
      .addCase(setDefault.pending, (state) => {
        state.isSetDefaulting = ApiLoadingStateEnum.loading;
      })
      .addCase(setDefault.rejected, (state, action) => {
        state.isSetDefaulting = ApiLoadingStateEnum.failed;
        state.errorResponse = action.payload as ErrorResponse;
      })

      // Remove
      .addCase(remove.fulfilled, (state, action) => {
        applyRemoveFulfilled(state, action.payload);
      })
      .addCase(remove.pending, (state) => {
        state.isRemoving = ApiLoadingStateEnum.loading;
      })
      .addCase(remove.rejected, (state, action) => {
        state.isRemoving = ApiLoadingStateEnum.failed;
        state.errorResponse = action.payload as ErrorResponse;
      });
  },
  initialState: initialBankDetailsState,
  name: 'bank-detail',
  reducers: {
    reset: () => {
      return {
        ...initialBankDetailsState,
      };
    },
    resetBankDetailState: (state) => {
      return {
        ...state,
        bankDetail: initialBankDetailsState.bankDetail,
      };
    },
    resetCreatingLoadingState: (state) => {
      return {
        ...state,
        // Add more loading state here
        isCreating: ApiLoadingStateEnum.idle,
      };
    },
    resetErrorResponse: (state) => {
      return {
        ...state,
        errorResponse: initialBankDetailsState.errorResponse,
      };
    },
    resetLoadingState: (state) => {
      return {
        ...state,
        // Add more loading state here
        isCreating: ApiLoadingStateEnum.idle,
        isFindOneLoading: ApiLoadingStateEnum.idle,
        isLoading: ApiLoadingStateEnum.idle,
        isRemoving: ApiLoadingStateEnum.idle,
        isUpdating: ApiLoadingStateEnum.idle,
      };
    },
    resetRemovingLoadingState: (state) => {
      return {
        ...state,
        // Add more loading state here
        isRemoving: ApiLoadingStateEnum.idle,
      };
    },
    resetSetDefaultLoadingState: (state) => {
      return {
        ...state,
        // Add more loading state here
        isSetDefaulting: ApiLoadingStateEnum.idle,
      };
    },
    resetUpdatingLoadingState: (state) => {
      return {
        ...state,
        // Add more loading state here
        isUpdating: ApiLoadingStateEnum.idle,
      };
    },
  },
});

const bankDetailsThunk = {
  create,
  createV2,
  findAll,
  findOne,
  remove,
  setDefault,
  update,
  updateV2,
};

const bankDetailsSelectors = {
  bankDetail: (state: RootState) => state.bankDetails.bankDetail,
  bankDetails: (state: RootState) => state.bankDetails.bankDetails,
  errorResponse: (state: RootState) => state.bankDetails.errorResponse,
  isCreating: (state: RootState) => state.bankDetails.isCreating,
  isFindOneLoading: (state: RootState) => state.bankDetails.isFindOneLoading,
  isLoading: (state: RootState) => state.bankDetails.isLoading,
  isRemoving: (state: RootState) => state.bankDetails.isRemoving,
  isSetDefaulting: (state: RootState) => state.bankDetails.isSetDefaulting,
  isUpdating: (state: RootState) => state.bankDetails.isUpdating,
};

export const bankDetailsService = {
  ...bankDetailsThunk,
  actions: bankDetailSlice.actions,
  selectors: bankDetailsSelectors,
};

export default bankDetailSlice.reducer;
